import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource, resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

import {
  applyFetchSpanName,
  applyXhrSpanName,
} from '@application/common-util/telemetry/http-span-naming';

import { isE2EEnvironment } from './e2e-test-ports';
import { getEnvironment, getExporterUrl } from './instrumentation-client';

// Re-exported from a dependency-free leaf module so consumers that only need the
// E2E port data (e.g. a Playwright config) can import it without pulling in the
// OpenTelemetry graph below. See `./e2e-test-ports` for why that matters.
export { E2E_TEST_PORTS, isE2EEnvironment } from './e2e-test-ports';

export type TelemetryConfig = {
  serviceName: string;
  environment?: string;
  exporterUrl?: string;
};

/**
 * Initializes OpenTelemetry for the browser with the specified configuration.
 * This should be called once when the application starts, typically in the instrumentation file.
 *
 * @param config Configuration for the telemetry client
 * @example
 * ```typescript
 * import { initializeTelemetry } from '@application/frontend-util/telemetry-client';
 *
 * initializeTelemetry({ serviceName: 'my-app' });
 * ```
 */

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Matches Cartesian domain URLs, excluding auth subdomains and the OTel collector */
export function buildPropagateTraceHeaderCorsUrlsRegex(
  traceExporterUrl: string,
): RegExp[] {
  const escapedUrl = escapeRegExp(traceExporterUrl);
  return [
    new RegExp(
      `^(?!${escapedUrl})https?://(?!auth[./])([^.]+\\.)*cartesian(\\.io|-[^.]+\\.click)(/|:\\d+|$)`,
    ),
    /^https?:\/\/localhost(:\d+)?(\/|$)/,
  ];
}

export function initializeTelemetry(config: TelemetryConfig): void {
  // Skip telemetry in E2E tests (identified by fixed port)
  if (isE2EEnvironment()) {
    console.log(
      `Skipping telemetry initialization for ${config.serviceName} - E2E test environment`,
    );
    return;
  }

  const environment = config.environment ?? getEnvironment() ?? 'unknown';
  const exporterUrl = config.exporterUrl ?? getExporterUrl(environment);

  const traceExporterUrl = `${exporterUrl}/v1/traces`;

  const resource: Resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
  });

  const traceExporter = new OTLPTraceExporter({
    url: traceExporterUrl,
  });

  const provider = new WebTracerProvider({
    spanProcessors: [new BatchSpanProcessor(traceExporter)],
    resource,
  });

  console.log(`Initializing telemetry for ${config.serviceName} client`);
  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator(),
      ],
    }),
  });

  const propagateTraceHeaderCorsUrlsRegex =
    buildPropagateTraceHeaderCorsUrlsRegex(traceExporterUrl);
  // Register browser instrumentations to automatically create spans
  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(), // Tracks page load performance
      new FetchInstrumentation({
        ignoreUrls: [traceExporterUrl],
        propagateTraceHeaderCorsUrls: propagateTraceHeaderCorsUrlsRegex,
        clearTimingResources: true,
        applyCustomAttributesOnSpan: applyFetchSpanName,
      }),
      new XMLHttpRequestInstrumentation({
        ignoreUrls: [traceExporterUrl],
        propagateTraceHeaderCorsUrls: propagateTraceHeaderCorsUrlsRegex,
        applyCustomAttributesOnSpan: applyXhrSpanName,
      }),
    ],
  });

  console.log('Browser instrumentations registered');
}
